// Revisión de un depósito sin confirmar: sí se depositó (con folio) o no se depositó.
import { useState } from 'react';
import { toast } from 'sonner';
import type { RetailCommission } from '@fiado/type-kit';
import { userMessageOf } from '@/api/errors';
import { Button, Input, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { useResolvePayment } from '../../api/commissionQueries';
import {
    toResolveRequest,
    validateResolution,
    type ResolutionErrors,
} from '../../lib/paymentForms';
import { FIELD, FIELD_ERROR } from '../fieldStyles';

interface ResolvePaymentDialogProps {
    payment: RetailCommission.CommissionPayment;
    sellerName: string;
    accredited: boolean;
    onClose: () => void;
}

export function ResolvePaymentDialog({
    payment,
    sellerName,
    accredited,
    onClose,
}: ResolvePaymentDialogProps) {
    const [folio, setFolio] = useState('');
    const [note, setNote] = useState('');
    const [errors, setErrors] = useState<ResolutionErrors>({});
    // Los avisos van en la mutación: corren aunque el diálogo ya se haya cerrado.
    const resolve = useResolvePayment({
        onSuccess: () => {
            toast.success(accredited ? 'Depósito confirmado' : 'Marcado como no depositado', {
                description: accredited
                    ? 'Entra al estado de cuenta del periodo en que se confirmó.'
                    : 'El monto vuelve al pendiente y entra en el siguiente pago.',
            });
            onClose();
        },
        onError: (error) => {
            toast.error(userMessageOf(error));
        },
    });

    const cancel = () => {
        if (!resolve.isPending) onClose();
    };

    const submit = () => {
        const found = validateResolution(accredited, folio, note);
        setErrors(found);
        if (found.folio !== undefined || found.note !== undefined) return;
        resolve.mutate({
            paymentId: payment.paymentId,
            body: toResolveRequest(accredited, folio, note),
        });
    };

    return (
        <Modal
            isOpen
            onClose={cancel}
            title={`${accredited ? 'Sí se depositó' : 'No se depositó'} · ${sellerName}`}
            size="sm"
        >
            <form
                noValidate
                className="flex flex-col gap-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <p className="text-[12px]">
                    Depósito de <b>{formatMoneyCents(payment.amountCents)}</b> solicitado el{' '}
                    {formatDateTimeMx(payment.requestedAt)}.
                </p>
                {!accredited && (
                    <p className="text-[11.5px] font-semibold text-danger">
                        Si el depósito sí llegó, el vendedor cobraría dos veces.
                    </p>
                )}
                {accredited && (
                    <Input
                        id="resolve-folio"
                        label="Folio"
                        placeholder="Folio del depósito"
                        maxLength={40}
                        autoComplete="off"
                        value={folio}
                        disabled={resolve.isPending}
                        onChange={(event) => {
                            setFolio(event.target.value);
                        }}
                        error={errors.folio}
                    />
                )}
                <div>
                    <label htmlFor="resolve-note" className="text-sm font-medium text-foreground">
                        Nota
                    </label>
                    <textarea
                        id="resolve-note"
                        rows={3}
                        maxLength={500}
                        className={cn(FIELD, 'mt-1 h-auto py-1')}
                        placeholder="Cómo se confirmó, para la auditoría"
                        disabled={resolve.isPending}
                        aria-invalid={errors.note ? true : undefined}
                        aria-describedby={errors.note ? 'resolve-note-error' : undefined}
                        value={note}
                        onChange={(event) => {
                            setNote(event.target.value);
                        }}
                    />
                    {errors.note !== undefined && (
                        <p id="resolve-note-error" className={FIELD_ERROR}>
                            {errors.note}
                        </p>
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={resolve.isPending}
                        onClick={cancel}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={resolve.isPending}>
                        Confirmar
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
